// src/components/features/UpcomingServices/UpcomingServiceFilters/UpcomingServiceFilters.tsx
//
// Upcoming-service-specific filters wrapper around ExpenseFiltersToolbar.
// Adds urgency status filter as extraFilters (in drawer) and extraChips
// (in the active filters row).
//
// Follows the same draft state pattern as TravelFilters:
// - Maintains draft values for urgencyStatuses internally.
// - Exposes an ExtraFiltersHandle via ref so ExpenseFiltersDrawer can
//   call onOpen (sync applied → draft), onApply (commit draft → applied),
//   onClear (reset drafts), and hasDraftValues (for Clear button visibility).
// - extraFilters rendered in the drawer bind to draft values.
// - extraChips bind to the APPLIED values (from props).

import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Badge, CloseButton, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { URGENCY_STATUS_OPTIONS } from "@/config/urgencyStatuses";

import { UrgencyStatus } from "@/utils/api/ServiceIntervalNextService";

import { ExpenseFiltersToolbar } from "@/components/features/ExpenseFiltersToolbar";
import { ExtraFiltersHandle } from "@/components/features/ExpenseFiltersToolbar/ExpenseFiltersDrawer";

import { UrgencyMultiSelector } from "../UrgencyMultiSelector";

// ============================================
// Types
// ============================================

export interface UpcomingServiceFiltersProps {
  /** Handler for refresh button click */
  onRefresh?: () => void;

  /** Whether refresh button is disabled */
  refreshDisabled?: boolean;

  /** Whether data is loading */
  loading?: boolean;

  /** Selected applied urgency statuses */
  urgencyStatuses?: UrgencyStatus[];

  /** Callback when urgency statuses are applied */
  onUrgencyStatusesChange?: (statuses: UrgencyStatus[]) => void;

  /** Show car filter (default: true) */
  showCarFilter?: boolean;

  /** Enable keyboard shortcuts (default: true) */
  enableShortcuts?: boolean;

  /**
   * Callback emitting a combined filter object built from all enabled sources.
   * Passed through to ExpenseFiltersToolbar.
   */
  onFiltersChange?: (filters: Record<string, unknown> | null) => void;
}

// ============================================
// Component
// ============================================

const UpcomingServiceFiltersComponent: React.FC<UpcomingServiceFiltersProps> = ({
  onRefresh,
  refreshDisabled = false,
  loading = false,
  urgencyStatuses = [],
  onUrgencyStatusesChange,
  showCarFilter = true,
  enableShortcuts = true,
  onFiltersChange,
}) => {
  const { t } = useTranslation(["services", "common"]);

  // ============================================
  // Draft State (used inside the drawer only)
  // ============================================

  const [draftUrgencyStatuses, setDraftUrgencyStatuses] = useState<UrgencyStatus[]>([]);

  // Ref to track latest applied values for getValues
  const urgencyStatusesRef = useRef(urgencyStatuses);
  urgencyStatusesRef.current = urgencyStatuses;

  // ============================================
  // ExtraFiltersHandle — lifecycle contract with ExpenseFiltersDrawer
  // ============================================

  const extraFiltersRef = useRef<ExtraFiltersHandle>({
    onOpen: () => {
      setDraftUrgencyStatuses(urgencyStatuses);
    },
    onApply: () => {
      onUrgencyStatusesChange?.(draftUrgencyStatuses);
      const values: Record<string, unknown> = {};
      if (draftUrgencyStatuses.length > 0) {
        values.urgencyStatus = draftUrgencyStatuses;
      }
      return values;
    },
    onClear: () => {
      setDraftUrgencyStatuses([]);
    },
    hasDraftValues: () => {
      return draftUrgencyStatuses.length > 0;
    },
    getValues: () => {
      const values: Record<string, unknown> = {};
      if (urgencyStatusesRef.current.length > 0) {
        values.urgencyStatus = urgencyStatusesRef.current;
      }
      return values;
    },
  });

  // Keep the ref callbacks up to date with latest state/props
  extraFiltersRef.current.onOpen = () => {
    setDraftUrgencyStatuses(urgencyStatuses);
  };
  extraFiltersRef.current.onApply = () => {
    onUrgencyStatusesChange?.(draftUrgencyStatuses);
    const values: Record<string, unknown> = {};
    if (draftUrgencyStatuses.length > 0) {
      values.urgencyStatus = draftUrgencyStatuses;
    }
    return values;
  };
  extraFiltersRef.current.onClear = () => {
    setDraftUrgencyStatuses([]);
  };
  extraFiltersRef.current.hasDraftValues = () => {
    return draftUrgencyStatuses.length > 0;
  };
  extraFiltersRef.current.getValues = () => {
    const values: Record<string, unknown> = {};
    if (urgencyStatusesRef.current.length > 0) {
      values.urgencyStatus = urgencyStatusesRef.current;
    }
    return values;
  };

  // ============================================
  // Handlers — Draft changes (inside drawer)
  // ============================================

  const handleDraftUrgencyChange = useCallback(
    (values: UrgencyStatus[]) => {
      setDraftUrgencyStatuses(values);
    },
    []
  );

  // ============================================
  // Handlers — Chip removal (removes applied values directly)
  // ============================================

  const handleRemoveUrgencyStatus = useCallback(
    (statusToRemove: UrgencyStatus) => {
      onUrgencyStatusesChange?.(
        urgencyStatuses.filter((s) => s !== statusToRemove)
      );
    },
    [urgencyStatuses, onUrgencyStatusesChange]
  );

  // ============================================
  // Build Extra Chips (based on APPLIED values, not drafts)
  // ============================================

  const extraChips = useMemo(() => {
    if (urgencyStatuses.length === 0) return undefined;

    const chips: React.ReactNode[] = [];

    urgencyStatuses.forEach((statusValue) => {
      const config = URGENCY_STATUS_OPTIONS.find(
        (opt) => opt.value === statusValue
      );
      if (!config) return;

      const label = t(config.translationKey, { defaultValue: config.value });

      chips.push(
        <Badge
          key={`urgency-${statusValue}`}
          variant="light"
          color={config.color}
          size="lg"
          radius="sm"
          pr={3}
          style={{ flexShrink: 0 }}
          rightSection={
            <CloseButton
              size="xs"
              variant="transparent"
              onClick={() => handleRemoveUrgencyStatus(statusValue)}
              aria-label={t("common:buttons.clear")}
            />
          }
        >
          {label}
        </Badge>
      );
    });

    if (chips.length === 0) return undefined;
    return <>{chips}</>;
  }, [urgencyStatuses, handleRemoveUrgencyStatus, t]);

  // ============================================
  // Build Extra Filters Content (for drawer — uses DRAFT values)
  // ============================================

  const extraFilters = useMemo(() => {
    return (
      <Stack gap="md">
        <Box>
          <UrgencyMultiSelector
            value={draftUrgencyStatuses}
            onChange={handleDraftUrgencyChange}
            label={t("services:urgencyFilter.label", {
              defaultValue: "Urgency",
            })}
            showLabel
            variant="inline"
          />
        </Box>
      </Stack>
    );
  }, [draftUrgencyStatuses, handleDraftUrgencyChange, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <ExpenseFiltersToolbar
      entityType="upcoming-services"
      onRefresh={onRefresh}
      refreshDisabled={refreshDisabled}
      loading={loading}
      carFilterMode={showCarFilter ? "single" : "none"}
      showDateFilter={false}
      showTagFilter={false}
      enableShortcuts={enableShortcuts}
      extraFilters={extraFilters}
      extraChips={extraChips}
      extraFiltersRef={extraFiltersRef}
      onFiltersChange={onFiltersChange}
    />
  );
};

export const UpcomingServiceFilters = React.memo(UpcomingServiceFiltersComponent);